import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AppState } from '../interfaces/app';
import { readFilteredEntity } from '../services/crudService';
import { User } from '../interfaces/entities/user';
import { Collection } from '../enums/collections.enum';
import { UserRoles } from '../enums/userRoles.enum';
import { adminEmail } from '../utils/config';

export const initialAuthState: AppState = {
  user: null,
  isAuthenticated: false,
  impersonatedByAdmin: null,
};

// ⚠️ SECURITY WARNING: The following simpleHash function is NOT secure for production use!
// It uses a weak hashing algorithm that is vulnerable to rainbow table attacks.
// In production, use:
// 1. Server-side hashing with bcrypt/argon2
// 2. Firebase Authentication for password management
// 3. Or proper cryptographic libraries like 'crypto-js' with salt

// This implementation is for DEMO/DEVELOPMENT purposes only
// TODO: Replace with proper authentication in production
export const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'hash_' + Math.abs(hash).toString(16);
};

// Compare plain password with stored hash
// ⚠️ TODO: Implement proper password verification in production
const comparePassword = (plainPassword: string, storedHash: string): boolean => {
  // In production, this should be done server-side with bcrypt.compare
  // or use Firebase Authentication's built-in verification
  return simpleHash(plainPassword) === storedHash;
};

// Set password with hash
// ⚠️ WARNING: This should NOT be used client-side in production
export const hashPassword = (password: string): string => {
  return simpleHash(password);
};

export const login = createAsyncThunk<
  User,
  { email: string; password: string },
  { rejectValue: string }
>(
  'auth/login',
  async ({ email, password }, thunkAPI) => {
    try {
      // Find user by email in Firestore
      const users = await readFilteredEntity<User>(Collection.Users, [
        { field: 'email', operator: '==', value: email }
      ]);

      if (!users || users.length === 0) {
        return thunkAPI.rejectWithValue('User not found with this email');
      }

      const dbUser = users[0];

      // Check if password matches — reject if no stored password or mismatch
      if (!dbUser.password || !comparePassword(password, dbUser.password)) {
        return thunkAPI.rejectWithValue('Invalid password');
      }

      // Role-based access control
      if (dbUser.role === UserRoles.admin && dbUser.email !== adminEmail) {
        return thunkAPI.rejectWithValue('Unknown error');
      } else if (dbUser.role !== UserRoles.admin && dbUser.role !== UserRoles.owner) {
        // Owners can view the employee details and have an edit option to edit details
        return thunkAPI.rejectWithValue('Only owners are allowed to login');
      } else if (dbUser.role !== UserRoles.admin && !dbUser.companyId) {
        return thunkAPI.rejectWithValue('User is not assigned to a company');
      } else if (!dbUser.enabled) {
        return thunkAPI.rejectWithValue('User is not activated! Please contact administrator');
      }

      return dbUser;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Login failed');
    }
  }
);

// Logout Action
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, thunkAPI) => {
    try {
      localStorage.clear();
      return true;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.impersonatedByAdmin = null;
    },
    impersonateUser: (state, action: PayloadAction<{ adminUser: User; targetUser: User }>) => {
      state.impersonatedByAdmin = action.payload.adminUser;
      state.user = action.payload.targetUser;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.impersonatedByAdmin = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.impersonatedByAdmin = null;
      });
  },
});

export const { setUser, impersonateUser } = authSlice.actions;
export const authReducer = authSlice.reducer;

